import { emailIsValid } from "@redotech/email";
import { toIsoAlpha2 } from "@redotech/locale/countries";
import {
  PortalShadow,
  PortalTextCase,
  portalCustomizationZodSchema,
} from "@redotech/redo-customer-portal-common/portal-customization";
import * as z from "zod";

// =============================================================================
// Return Policy (step: ret-return-policy)
// Merges: ReturnWindow + Eligibility + Exclusions
// =============================================================================

const EligibilityRequirementSchema_ = z.object({
  id: z.string(),
  label: z.string(),
});
export const EligibilityRequirementSchema: z.ZodType<EligibilityRequirement> =
  EligibilityRequirementSchema_;
export interface EligibilityRequirement extends z.infer<
  typeof EligibilityRequirementSchema_
> {}

export const MAX_ELIGIBILITY_REQUIREMENTS = 4;

const ReturnPolicyFormSchema_ = z.object({
  daysAfterDelivery: z.number().min(0).max(365),
  eligibilityRequirements: z
    .array(EligibilityRequirementSchema)
    .max(MAX_ELIGIBILITY_REQUIREMENTS),
  excludeByTags: z.array(z.string()).default([]),
  excludeByCollections: z.array(z.string()).default([]),
  excludeBySkus: z.array(z.string()).default([]),
  policyUrl: z.string().optional(),
});
export const ReturnPolicyFormSchema: z.ZodType<ReturnPolicyForm> =
  ReturnPolicyFormSchema_;
export interface ReturnPolicyForm extends z.infer<
  typeof ReturnPolicyFormSchema_
> {}

// =============================================================================
// Return Options (stored under ret-return-options by Return Policy)
// Merges: ReturnMethods + ReturnProcessing + ShippingResponsibility
// =============================================================================

const BONUS_TYPES = ["percentage", "flat"] as const;
const PROCESSING_TIMINGS = [
  "return-created",
  "shipment-transit",
  "shipment-delivered",
  "manual",
] as const;
const SHIPPING_RESPONSIBILITIES = ["customer", "merchant"] as const;

export const POLICY_SHIPPING_RESPONSIBILITY_CONFIGURED_KEY =
  "policyShippingResponsibilityConfigured";

const FeeConfigSchema_ = z.object({
  enabled: z.boolean(),
  amount: z.number().nullable(),
  type: z.enum(BONUS_TYPES),
});
export const FeeConfigSchema: z.ZodType<FeeConfig> = FeeConfigSchema_;
export interface FeeConfig extends z.infer<typeof FeeConfigSchema_> {}

const MethodProcessingSchema_ = z.object({
  processingTiming: z.enum(PROCESSING_TIMINGS),
  processingDelayDays: z.number().optional(),
});
export const MethodProcessingSchema: z.ZodType<MethodProcessing> =
  MethodProcessingSchema_;
export interface MethodProcessing extends z.infer<
  typeof MethodProcessingSchema_
> {}

const ReturnOptionsFormSchema_ = z.object({
  refundEnabled: z.boolean(),
  refundProcessingFee: FeeConfigSchema,
  refundProcessing: MethodProcessingSchema,

  storeCreditEnabled: z.boolean(),
  storeCreditBonus: FeeConfigSchema,
  storeCreditProcessing: MethodProcessingSchema,

  exchangeEnabled: z.boolean(),
  exchangeBonus: FeeConfigSchema,
  exchangeProcessing: MethodProcessingSchema,

  returnShippingResponsibility: z.enum(SHIPPING_RESPONSIBILITIES),
  exchangeShippingResponsibility: z.enum(SHIPPING_RESPONSIBILITIES),
});
export const ReturnOptionsFormSchema: z.ZodType<ReturnOptionsForm> =
  ReturnOptionsFormSchema_;
export interface ReturnOptionsForm extends z.infer<
  typeof ReturnOptionsFormSchema_
> {}

// =============================================================================
// Return Reasons (step: ret-return-reasons)
// =============================================================================

const ReturnReasonsFormSchema_ = z.object({
  wrongProductManualReview: z.boolean(),
  damagedManualReview: z.boolean(),
  defaultManualReview: z.boolean(),
});
export const ReturnReasonsFormSchema: z.ZodType<ReturnReasonsForm> =
  ReturnReasonsFormSchema_;
export interface ReturnReasonsForm extends z.infer<
  typeof ReturnReasonsFormSchema_
> {}

// =============================================================================
// Return Address (stored under ret-return-address by Communications)
// =============================================================================

const ReturnAddressFormSchema_ = z.object({
  line1: z.string().min(1),
  line2: z.string().optional(),
  city: z.string().min(1),
  state: z.string().min(1),
  zip: z.string().min(1),
  country: z.string().min(1),
  company: z.string().optional(),
});
export const ReturnAddressFormSchema: z.ZodType<ReturnAddressForm> =
  ReturnAddressFormSchema_;
export interface ReturnAddressForm extends z.infer<
  typeof ReturnAddressFormSchema_
> {}

// =============================================================================
// Portal Preview (step: ret-portal-preview)
// =============================================================================

const PortalPreviewCustomizationSchema_ = portalCustomizationZodSchema.pick({
  branding: true,
  navbar: true,
  typography: true,
  buttons: true,
  inputFields: true,
  style: true,
});
export const PortalPreviewCustomizationSchema: z.ZodType<PortalPreviewCustomization> =
  PortalPreviewCustomizationSchema_;
export interface PortalPreviewCustomization extends z.infer<
  typeof PortalPreviewCustomizationSchema_
> {}

const PortalPreviewFormSchema_ = z.object({
  portalBackgroundColor: z
    .string()
    .regex(/^#[0-9a-f]{6}$/i, "Expected a six-digit hex color")
    .default("#ffffff"),
  headerBackgroundColor: z
    .string()
    .regex(/^#[0-9a-f]{6}$/i, "Expected a six-digit hex color")
    .default("#FFFFFF"),
  headerTextColor: z
    .string()
    .regex(/^#[0-9a-f]{6}$/i, "Expected a six-digit hex color")
    .default("#0F0F0F"),
  linkColor: z
    .string()
    .regex(/^#[0-9a-f]{6}$/i, "Expected a six-digit hex color")
    .default("#3366ff"),
  portalLogo: z.preprocess(
    (val) => (val === "" ? null : val),
    z.string().url().nullable().optional(),
  ),
  portalBackground: z.preprocess(
    (val) => (val === "" ? null : val),
    z.string().url().nullable().optional(),
  ),
  portalCustomization: PortalPreviewCustomizationSchema.optional(),
});
export const PortalPreviewFormSchema: z.ZodType<PortalPreviewForm> =
  PortalPreviewFormSchema_;
export interface PortalPreviewForm extends z.infer<
  typeof PortalPreviewFormSchema_
> {}

// =============================================================================
// Communications (step: ret-communications)
// =============================================================================

const SingleEmailFieldSchema = z
  .string()
  .trim()
  .refine((value) => !value.includes(",") && emailIsValid(value), {
    message: "Invalid email",
  });

export const MultiEmailFieldSchema = z
  .string()
  .trim()
  .refine(emailIsValid, { message: "Invalid email" });

const CommunicationsFormSchema_ = z.object({
  senderEmail: SingleEmailFieldSchema,
  notifyReturnCreated: z.boolean(),
  notifyReturnDelivered: z.boolean(),
  notifyReturnNeedsReview: z.boolean(),
  notificationRecipientEmail: MultiEmailFieldSchema,
  billingEmail: MultiEmailFieldSchema,
});
export const CommunicationsFormSchema: z.ZodType<CommunicationsForm> =
  CommunicationsFormSchema_;
export interface CommunicationsForm extends z.infer<
  typeof CommunicationsFormSchema_
> {}

// =============================================================================
// Coverage Widget Config (step: ret-coverage-widget)
// Received via postMessage from the Shopify storefront configurator.
// =============================================================================

const CoverageWidgetCoverageConfigSchema_ = z.object({
  iconType: z.string(),
  customLogoUrl: z.string().optional(),
  titleText: z.string(),
  titleFontSize: z.number(),
  titleColor: z.string(),
  subtitleText: z.string(),
  subtitleFontSize: z.number(),
  subtitleColor: z.string(),
  backgroundColor: z.string(),
  cardBorderRadius: z.number(),
  buttonFontSize: z.number(),
  buttonColor: z.string(),
  buttonTextColor: z.string(),
  buttonBorderRadius: z.number(),
  optOutText: z.string(),
  optOutFontSize: z.number(),
  optOutTextColor: z.string(),
});
export const CoverageWidgetCoverageConfigSchema: z.ZodType<CoverageWidgetCoverageConfig> =
  CoverageWidgetCoverageConfigSchema_;
export interface CoverageWidgetCoverageConfig extends z.infer<
  typeof CoverageWidgetCoverageConfigSchema_
> {}

const CoverageWidgetInfoModalConfigSchema_ = z.object({
  leftSideImage: z.string().optional(),
  logo: z.string().optional(),
  headingText: z.string().optional(),
  bodyText: z.string().optional(),
  benefits: z
    .array(z.object({ text: z.string(), subtext: z.string() }))
    .optional(),
});
export const CoverageWidgetInfoModalConfigSchema: z.ZodType<CoverageWidgetInfoModalConfig> =
  CoverageWidgetInfoModalConfigSchema_;
export interface CoverageWidgetInfoModalConfig extends z.infer<
  typeof CoverageWidgetInfoModalConfigSchema_
> {}

const CoverageWidgetFormSchema_ = z.object({
  selector: z.string().trim().min(1, { message: "Selector is required" }),
  insertionPosition: z.enum(["before", "after", "prepend", "append"]),
  coverageConfig: CoverageWidgetCoverageConfigSchema,
  checkoutButtonText: z.string(),
  checkoutButtonSelector: z.string().optional(),
  infoModalConfig: CoverageWidgetInfoModalConfigSchema.optional(),
});
export const CoverageWidgetFormSchema: z.ZodType<CoverageWidgetForm> =
  CoverageWidgetFormSchema_;
export interface CoverageWidgetForm extends z.infer<
  typeof CoverageWidgetFormSchema_
> {}

export const ReturnsCoverageBuilderProgressMarkerSchema = z.object({
  version: z.literal(2),
});

// =============================================================================
// Product availability (returned by getProgress.productConfig)
// =============================================================================

const ReturnsAvailabilitySchema_ = z.object({
  coverageEnabled: z.boolean(),
  coverageBasePrice: z.number(),
  hasExistingReturnPolicy: z.boolean(),
  merchantCountryCode: z.string().optional(),
  storeAddress: ReturnAddressFormSchema_.partial().optional(),
  detectedPolicyUrl: z.string().optional(),
  /** False for merchants without a Shopify store. Absent means Shopify. */
  isShopify: z.boolean().optional(),
});
export const ReturnsAvailabilitySchema: z.ZodType<ReturnsAvailability> =
  ReturnsAvailabilitySchema_;
export interface ReturnsAvailability extends z.infer<
  typeof ReturnsAvailabilitySchema_
> {}

// =============================================================================
// Defaults
// =============================================================================

/**
 * Default coverage price (dollars as string). Written to team `pricePerOrder`
 * on returns onboarding completion — not `settings.returns.pricePerOrder`.
 */
export const DEFAULT_COVERAGE_PRICE = "2.48";

export const DEFAULT_ELIGIBILITY_REQUIREMENTS: EligibilityRequirement[] = [];

export const DEFAULT_RETURN_POLICY: ReturnPolicyForm = {
  daysAfterDelivery: 30,
  eligibilityRequirements: [...DEFAULT_ELIGIBILITY_REQUIREMENTS],
  excludeByTags: [],
  excludeByCollections: [],
  excludeBySkus: [],
};

export function normalizeEligibilityRequirements(
  reqs: EligibilityRequirement[],
): EligibilityRequirement[] {
  return reqs.reduce<EligibilityRequirement[]>((acc, requirement) => {
    const label = requirement.label.trim();
    if (!label) {
      return acc;
    }

    acc.push({ id: requirement.id, label });
    return acc;
  }, []);
}

export function normalizeReturnPolicyForm(
  policy: ReturnPolicyForm,
): ReturnPolicyForm {
  return {
    ...policy,
    eligibilityRequirements: normalizeEligibilityRequirements(
      policy.eligibilityRequirements,
    ),
  };
}

function lowercaseEligibilityContinuation(label: string): string {
  return label.replace(
    /^(\s*)([A-Z][a-z]+)\b/,
    (_match, leadingWhitespace: string, firstWord: string) =>
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      `${leadingWhitespace}${firstWord[0]!.toLowerCase()}${firstWord.slice(1)}`,
  );
}

export function buildEligibilityMessage(
  reqs: EligibilityRequirement[],
): string {
  const labels = normalizeEligibilityRequirements(reqs).map(
    (requirement, index) =>
      index === 0
        ? requirement.label
        : lowercaseEligibilityContinuation(requirement.label),
  );
  if (labels.length === 0) {
    return "";
  }
  if (labels.length === 1) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return labels[0]!;
  }
  if (labels.length === 2) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return `${labels[0]!} & ${labels[1]!}`;
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return `${labels.slice(0, -1).join(", ")}, & ${labels[labels.length - 1]!}`;
}

const DEFAULT_FEE: FeeConfig = {
  enabled: false,
  amount: null,
  type: "percentage",
};
const DEFAULT_REFUND_PROCESSING: MethodProcessing = {
  processingTiming: "manual",
};
const DEFAULT_RETURNED_ITEM_PROCESSING: MethodProcessing = {
  processingTiming: "shipment-delivered",
};

export const DEFAULT_RETURN_OPTIONS: ReturnOptionsForm = {
  refundEnabled: true,
  refundProcessingFee: { ...DEFAULT_FEE },
  refundProcessing: { ...DEFAULT_REFUND_PROCESSING },

  storeCreditEnabled: true,
  storeCreditBonus: { ...DEFAULT_FEE },
  storeCreditProcessing: { ...DEFAULT_RETURNED_ITEM_PROCESSING },

  exchangeEnabled: true,
  exchangeBonus: { ...DEFAULT_FEE },
  exchangeProcessing: { ...DEFAULT_RETURNED_ITEM_PROCESSING },

  returnShippingResponsibility: "customer",
  exchangeShippingResponsibility: "customer",
};

export function resolveOnboardingShippingResponsibilities(
  returnOptions: ReturnOptionsForm,
  formData: Record<string, Record<string, unknown> | undefined>,
): ReturnOptionsForm {
  const policyConfigured =
    formData["ret-meta"]?.[POLICY_SHIPPING_RESPONSIBILITY_CONFIGURED_KEY] ===
    true;
  const legacyCoverageData = formData["ret-about-coverage"];
  if (policyConfigured || !legacyCoverageData) {
    return returnOptions;
  }

  const legacyReturnResponsibility =
    legacyCoverageData.returnShippingResponsibility;
  const legacyExchangeResponsibility =
    legacyCoverageData.exchangeShippingResponsibility;

  return {
    ...returnOptions,
    returnShippingResponsibility:
      legacyReturnResponsibility === "customer" ||
      legacyReturnResponsibility === "merchant"
        ? legacyReturnResponsibility
        : returnOptions.returnShippingResponsibility,
    exchangeShippingResponsibility:
      legacyExchangeResponsibility === "customer" ||
      legacyExchangeResponsibility === "merchant"
        ? legacyExchangeResponsibility
        : returnOptions.exchangeShippingResponsibility,
  };
}

export const DEFAULT_RETURN_REASONS: ReturnReasonsForm = {
  wrongProductManualReview: false,
  damagedManualReview: false,
  defaultManualReview: false,
};

export const DEFAULT_RETURN_ADDRESS: ReturnAddressForm = {
  line1: "",
  city: "",
  state: "",
  zip: "",
  country: "US",
};

export function shopCountryCodeFromShopify(
  shopify?: { country?: string | null; country_code?: string | null } | null,
): string | undefined {
  return (
    toIsoAlpha2(shopify?.country_code) ??
    toIsoAlpha2(shopify?.country) ??
    undefined
  );
}

export function resolveReturnAddressCountry(
  addressCountry?: string | null,
  merchantCountry?: string | null,
): string {
  return (
    toIsoAlpha2(addressCountry) ??
    toIsoAlpha2(merchantCountry) ??
    DEFAULT_RETURN_ADDRESS.country
  );
}

export function defaultReturnAddressForMerchant(
  merchantCountry?: string | null,
): ReturnAddressForm {
  return {
    ...DEFAULT_RETURN_ADDRESS,
    country: resolveReturnAddressCountry(undefined, merchantCountry),
  };
}

export function defaultCommunications(email: string): CommunicationsForm {
  return {
    senderEmail: email,
    notifyReturnCreated: true,
    notifyReturnDelivered: true,
    notifyReturnNeedsReview: true,
    notificationRecipientEmail: email,
    billingEmail: email,
  };
}

export const DEFAULT_PORTAL_PREVIEW: PortalPreviewForm = {
  portalBackgroundColor: "#ffffff",
  headerBackgroundColor: "#FFFFFF",
  headerTextColor: "#0F0F0F",
  linkColor: "#3366ff",
  portalCustomization: {
    branding: {
      backgroundColor: "#f9f9f9",
      portalBackgroundColor: "#ffffff",
      linkColor: "#3366ff",
    },
    navbar: { backgroundColor: "#FFFFFF", textColor: "#0F0F0F" },
    typography: {
      fonts: {},
      fontWeights: {},
      textColors: { primary: "#141414", secondary: "#525252" },
      textCase: PortalTextCase.DEFAULT,
    },
    buttons: {
      primary: {
        colors: { background: "#000000", border: "#666666", text: "#FFFFFF" },
        style: {
          cornerRadius: 8,
          borderWidth: 0,
          padding: 8,
          shadow: PortalShadow.NONE,
        },
      },
      secondary: {
        colors: { background: "#FFFFFF", border: "#D0D5DD", text: "#344054" },
        style: {
          cornerRadius: 8,
          borderWidth: 0,
          padding: 8,
          shadow: PortalShadow.NONE,
        },
      },
      tertiary: {
        colors: {
          background: "transparent",
          border: "transparent",
          text: "#344054",
        },
        style: {
          cornerRadius: 8,
          borderWidth: 0,
          padding: 8,
          shadow: PortalShadow.NONE,
        },
      },
    },
    inputFields: {
      colors: {
        background: "#FFFFFF",
        border: "#000000",
        text: "#000000",
        error: "#D92D20",
      },
      style: {
        cornerRadius: 8,
        borderWidth: 0,
        padding: 8,
        shadow: PortalShadow.NONE,
      },
    },
    style: {
      cornerRadius: 8,
      borderWidth: 0,
      colors: { selectedBackground: "#0F0F0F", selectedBorder: "#D6D6D6" },
      globalCard: {
        borderColor: "#D6D6D6",
        borderWidth: 0,
        borderRadius: 12,
        shadow: PortalShadow.NONE,
      },
    },
  },
};

export function resolvePortalPreviewCustomization(
  portalPreview: PortalPreviewForm,
  fallbackLogoUrl?: string,
): PortalPreviewCustomization {
  const defaults = DEFAULT_PORTAL_PREVIEW.portalCustomization;
  if (!defaults) {
    throw new Error("Portal preview customization defaults are required");
  }

  const saved = portalPreview.portalCustomization;
  return {
    branding: {
      ...defaults.branding,
      logoUrl: portalPreview.portalLogo ?? fallbackLogoUrl,
      backgroundImageUrl: portalPreview.portalBackground ?? undefined,
      portalBackgroundColor: portalPreview.portalBackgroundColor,
      linkColor: portalPreview.linkColor,
      ...saved?.branding,
    },
    navbar: {
      ...defaults.navbar,
      backgroundColor: portalPreview.headerBackgroundColor,
      textColor: portalPreview.headerTextColor,
      ...saved?.navbar,
    },
    typography: {
      ...defaults.typography,
      ...saved?.typography,
      fonts: { ...defaults.typography?.fonts, ...saved?.typography?.fonts },
      fontWeights: {
        ...defaults.typography?.fontWeights,
        ...saved?.typography?.fontWeights,
      },
      textColors: {
        ...defaults.typography?.textColors,
        ...saved?.typography?.textColors,
      },
    },
    buttons: {
      primary: {
        ...defaults.buttons?.primary,
        ...saved?.buttons?.primary,
        colors: {
          ...defaults.buttons?.primary?.colors,
          ...saved?.buttons?.primary?.colors,
        },
        style: {
          ...defaults.buttons?.primary?.style,
          ...saved?.buttons?.primary?.style,
        },
      },
      secondary: {
        ...defaults.buttons?.secondary,
        ...saved?.buttons?.secondary,
        colors: {
          ...defaults.buttons?.secondary?.colors,
          ...saved?.buttons?.secondary?.colors,
        },
        style: {
          ...defaults.buttons?.secondary?.style,
          ...saved?.buttons?.secondary?.style,
        },
      },
      tertiary: {
        ...defaults.buttons?.tertiary,
        ...saved?.buttons?.tertiary,
        colors: {
          ...defaults.buttons?.tertiary?.colors,
          ...saved?.buttons?.tertiary?.colors,
        },
        style: {
          ...defaults.buttons?.tertiary?.style,
          ...saved?.buttons?.tertiary?.style,
        },
      },
    },
    inputFields: {
      ...defaults.inputFields,
      ...saved?.inputFields,
      colors: {
        ...defaults.inputFields?.colors,
        ...saved?.inputFields?.colors,
      },
      style: { ...defaults.inputFields?.style, ...saved?.inputFields?.style },
    },
    style: {
      ...defaults.style,
      ...saved?.style,
      colors: { ...defaults.style?.colors, ...saved?.style?.colors },
      globalCard: {
        ...defaults.style?.globalCard,
        ...saved?.style?.globalCard,
      },
    },
  };
}
